import type React from "react";
import { type JSX, useId, useRef, useState } from "react";

import { useElementWidth } from "../hooks/useElementWidth";
import { fitText, measureText } from "../lib/measureText";

/** One grouped row, already reduced to what a bar needs. */
export interface UsageBar {
  /** The category this bar stands for — a model, an operation, a day, a project, an id. */
  label: string;
  /** The first stacked part, and the whole bar when there is no second series. */
  first: number;
  second: number;
  total: number;
  /** Extra rows for the tooltip and readout, e.g. a call count beside a token total. */
  extra?: { label: string; value: number }[];
}

/**
 * What the two stacked parts are called.
 *
 * Parameterised because this chart is used for more than tokens: a legend reading "Prompt /
 * Completion" over a count of memories would simply be false. Omitting `second` makes it a
 * single-series chart, which then carries no legend at all — with one colour there is nothing to
 * disambiguate, and the heading already says what is plotted.
 */
export interface SeriesLabels {
  first: string;
  second?: string | undefined;
}

interface UsageChartProps {
  bars: UsageBar[];
  series: SeriesLabels;
  /** Time reads left-to-right, so `day` becomes columns; everything else stays horizontal. */
  orientation: "horizontal" | "vertical";
  /** Clicking a mark narrows the query to that category. */
  onPick?: ((label: string) => void) | undefined;
}

/**
 * Prompt and completion tokens per category.
 *
 * Form: the two measures sum to the total, so this is part-to-whole per category — a stacked
 * bar, horizontal because category names here are model ids, operation names and ULIDs. `day`
 * is the one dimension with a natural direction, so it becomes columns.
 *
 * Colour: two series, so categorical slots 1 and 2. Both modes pass all six checks of the
 * palette validator against this app's own surfaces (#ffffff / #1c1c20) — worst adjacent CVD
 * ΔE 24.7 light, 26.8 dark, well clear of the 8 target.
 *
 * The values are also in the table view below the chart, so nothing here is reachable only by
 * hovering.
 */
export function UsageChart({ bars, series, orientation, onPick }: UsageChartProps): JSX.Element {
  const [hovered, setHovered] = useState<number | null>(null);
  // Where to float the tooltip. Null while the pointer is elsewhere; a keyboard focus sets the
  // hovered bar without a position, which is why this is separate state rather than derived.
  const [pointer, setPointer] = useState<{ x: number; y: number; width: number } | null>(null);
  const plot = useRef<HTMLDivElement>(null);
  const available = useElementWidth(plot);
  const clipId = useId();

  if (bars.length === 0) {
    return <p className="muted">Nothing recorded for this slice.</p>;
  }

  const max = Math.max(...bars.map((bar) => bar.total), 1);

  const track = (event: React.PointerEvent): void => {
    const box = plot.current?.getBoundingClientRect();
    if (box) {
      setPointer({ x: event.clientX - box.left, y: event.clientY - box.top, width: box.width });
    }
  };

  const enter = (index: number): void => setHovered(index);
  const leave = (): void => {
    setHovered(null);
    setPointer(null);
  };

  const shared = {
    bars,
    max,
    hovered,
    onPick,
    onEnter: enter,
    onLeave: leave,
    onMove: track,
    // Lay out against the space the panel actually gives us, not against the content -- but
    // never below the floor, so a narrow panel scrolls instead of squashing.
    width: Math.max(available, MIN_LAYOUT),
  };
  const active = hovered === null ? null : (bars[hovered] ?? null);

  return (
    <div className="chart">
      {series.second && <Legend series={series} />}

      <div className="chart__plot" ref={plot}>
        {/* Before the first measurement there is no width to lay out against, so the plot waits
            one frame rather than truncating every label to nothing. */}
        {available === 0 ? null : orientation === "horizontal" ? (
          <HorizontalBars {...shared} clipId={clipId} />
        ) : (
          <Columns {...shared} />
        )}

        {/* Pointer affordance only. Keyboard focus is served by the readout below, which carries
            the same numbers -- so nothing here is reachable by hovering alone. */}
        {active && pointer && <Tooltip bar={active} series={series} at={pointer} />}
      </div>

      {/* Always on. It used to appear and vanish with the pointer, which both hid the breakdown
          most of the time and shifted the page each way. With nothing hovered it describes the
          whole slice instead of disappearing. */}
      <Readout bar={active} bars={bars} series={series} />
    </div>
  );
}

/**
 * Floating readout at the pointer. Values lead, series names follow.
 *
 * Flips to the pointer's left near the right edge, so it never runs outside the plot. That has to
 * be measured rather than left to CSS: a max-width stops it overflowing but leaves it squashed
 * into a column two words wide, which is not the same thing.
 */
function Tooltip({
  bar,
  series,
  at,
}: {
  bar: UsageBar;
  series: SeriesLabels;
  at: { x: number; y: number; width: number };
}): JSX.Element {
  const flip = at.x > at.width - TOOLTIP_ROOM;
  return (
    <div
      className={`chart__tooltip${flip ? " chart__tooltip--flipped" : ""}`}
      style={{ left: at.x, top: at.y }}
      role="tooltip"
      aria-hidden="true"
    >
      <div className="chart__tooltip-head">{bar.label}</div>
      <dl>{rows(bar, series)}</dl>
    </div>
  );
}

/** Always present for two series: identity must never rest on colour alone. */
function Legend({ series }: { series: SeriesLabels }): JSX.Element {
  return (
    <ul className="chart__legend">
      <li>
        <span className="chart__swatch chart__swatch--prompt" aria-hidden="true" />
        {series.first}
      </li>
      <li>
        <span className="chart__swatch chart__swatch--completion" aria-hidden="true" />
        {series.second}
      </li>
    </ul>
  );
}

/** Space the tooltip needs to its right before it flips to the pointer's other side. */
const TOOLTIP_ROOM = 230;

const BAR_THICKNESS = 22;
const BAR_GAP = 14;
/** The 2px surface gap that separates the two stacked segments. */
const SEGMENT_GAP = 2;

/**
 * The font the axis labels render in, for measurement.
 *
 * Must match `.chart__category` / `.chart__value` in styles.css. If it drifts, labels are
 * truncated against the wrong width and start overlapping the bars again.
 */
const LABEL_FONT = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

/** Gap between a category label and the bar it names. */
const LABEL_PAD = 10;
/** How much of the width the category labels may take before they get truncated. */
const LABEL_SHARE = 0.34;
const LABEL_MIN = 56;
/** Enough for a bar's total, plus the gap to it. */
const VALUE_PAD = 10;
/** Below this the plot is not worth drawing; the readout and table still carry the numbers. */
const MIN_PLOT = 80;

/**
 * Narrowest layout the chart will draw at.
 *
 * A cramped panel gets a chart that scrolls sideways rather than no chart at all — laying out
 * against 200px would leave room for a label and nothing else.
 */
const MIN_LAYOUT = 380;

/** Widest rendered value label, so the plot never overruns its own numbers. */
function valueColumn(bars: UsageBar[]): number {
  const widest = Math.max(
    ...bars.map((bar) => measureText(bar.total.toLocaleString(), LABEL_FONT)),
    0,
  );
  return widest + VALUE_PAD * 2;
}

interface BarsProps {
  bars: UsageBar[];
  max: number;
  /** The measured width of the plot container, in CSS pixels. */
  width: number;
  hovered: number | null;
  onPick?: ((label: string) => void) | undefined;
  onEnter: (index: number) => void;
  onLeave: () => void;
  onMove: (event: React.PointerEvent) => void;
}

function HorizontalBars({
  bars,
  max,
  width,
  hovered,
  onPick,
  onEnter,
  onLeave,
  onMove,
  clipId,
}: BarsProps & { clipId: string }): JSX.Element {
  // Category labels get a share of the width, capped by the longest label that is actually
  // there, so short names do not reserve a wide empty gutter and ULIDs do not eat the plot.
  const longest = Math.max(...bars.map((bar) => measureText(bar.label, LABEL_FONT)), 0);
  const labelColumn = Math.max(
    Math.min(longest + LABEL_PAD, width * LABEL_SHARE),
    Math.min(LABEL_MIN, width * LABEL_SHARE),
  );
  const values = valueColumn(bars);
  const plotWidth = Math.max(width - labelColumn - values, MIN_PLOT);
  const height = bars.length * (BAR_THICKNESS + BAR_GAP);
  const scale = (value: number): number => (value / max) * plotWidth;
  const room = labelColumn - LABEL_PAD;

  return (
    <div className="chart__scroll">
      <svg
        viewBox={`0 0 ${labelColumn + plotWidth + values} ${height}`}
        width={labelColumn + plotWidth + values}
        height={height}
        role="img"
        aria-label="Token usage by category"
        className="chart__svg"
      >
        {bars.map((bar, index) => {
          const y = index * (BAR_THICKNESS + BAR_GAP);
          const promptWidth = scale(bar.first);
          const completionWidth = Math.max(scale(bar.second) - SEGMENT_GAP, 0);
          const active = hovered === index;
          return (
            <g
              key={`${index}:${bar.label}`}
              className={`chart__bar${active ? " chart__bar--active" : ""}${onPick ? " chart__bar--pickable" : ""}`}
              onPointerEnter={() => onEnter(index)}
              onPointerMove={onMove}
              onPointerLeave={onLeave}
              onFocus={() => onEnter(index)}
              onBlur={onLeave}
              onClick={() => onPick?.(bar.label)}
              onKeyDown={(event) => activate(event, () => onPick?.(bar.label))}
              tabIndex={onPick ? 0 : -1}
              role={onPick ? "button" : undefined}
            >
              {/* A hit target that covers the bar, its gap and the label. */}
              <rect
                x={0}
                y={y - BAR_GAP / 2}
                width={labelColumn + plotWidth + values}
                height={BAR_THICKNESS + BAR_GAP}
                fill="transparent"
              />

              <text
                x={labelColumn - LABEL_PAD}
                y={y + BAR_THICKNESS / 2}
                textAnchor="end"
                dominantBaseline="central"
                className="chart__category"
              >
                {/* Cut to the column, with the full text one hover away. */}
                <title>{bar.label}</title>
                {fitText(bar.label, room, LABEL_FONT)}
              </text>

              <clipPath id={`${clipId}-${index}`}>
                {/* Rounds only the data end; the baseline end stays square. */}
                <rect
                  x={labelColumn}
                  y={y}
                  width={Math.max(scale(bar.total), 2)}
                  height={BAR_THICKNESS}
                  rx={4}
                />
              </clipPath>

              <g clipPath={`url(#${clipId}-${index})`}>
                <rect
                  x={labelColumn}
                  y={y}
                  width={Math.max(promptWidth, 1)}
                  height={BAR_THICKNESS}
                  className="chart__fill chart__fill--prompt"
                />
                <rect
                  x={labelColumn + promptWidth + SEGMENT_GAP}
                  y={y}
                  width={completionWidth}
                  height={BAR_THICKNESS}
                  className="chart__fill chart__fill--completion"
                />
              </g>

              {/* One direct label per bar — the total at the tip, never a number per segment. */}
              <text
                x={labelColumn + scale(bar.total) + VALUE_PAD}
                y={y + BAR_THICKNESS / 2}
                dominantBaseline="central"
                className="chart__value"
              >
                {bar.total.toLocaleString()}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

const COLUMN_WIDTH = 24;
const COLUMN_GAP = 18;
const PLOT_HEIGHT = 180;
/** Headroom for the topmost tick label, which otherwise renders half outside the viewBox. */
const PLOT_TOP = 10;
const AXIS_WIDTH = 62;
/** Room under the plot for the category labels. */
const AXIS_LABEL_HEIGHT = 26;

function Columns({
  bars,
  max,
  width,
  hovered,
  onPick,
  onEnter,
  onLeave,
  onMove,
}: BarsProps): JSX.Element {
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1] ?? max;
  // Span the panel rather than the content. Sizing to the content made a single day render as a
  // 100px sliver in a wide card, which reads as a broken chart rather than as one day of data.
  const plotWidth = Math.max(width - AXIS_WIDTH, MIN_PLOT);
  const band = bars.length > 0 ? plotWidth / bars.length : plotWidth;
  // Columns stay capped at 24px however much room there is -- the band's leftover is air, not a
  // fatter bar -- but they narrow when days are tight so neighbours never touch.
  const column = Math.max(Math.min(COLUMN_WIDTH, band - COLUMN_GAP / 2), 4);
  const height = PLOT_TOP + PLOT_HEIGHT + AXIS_LABEL_HEIGHT;
  const scale = (value: number): number => (value / top) * PLOT_HEIGHT;
  const baseline = PLOT_TOP + PLOT_HEIGHT;

  return (
    <div className="chart__scroll">
      <svg
        viewBox={`0 0 ${AXIS_WIDTH + plotWidth} ${height}`}
        width={AXIS_WIDTH + plotWidth}
        height={height}
        role="img"
        aria-label="Token usage over time"
        className="chart__svg"
      >
        {/* Hairline gridlines, one step off the surface, carrying the values not directly labelled. */}
        {ticks.map((tick) => {
          const y = baseline - scale(tick);
          return (
            <g key={tick}>
              <line
                x1={AXIS_WIDTH}
                y1={y}
                x2={AXIS_WIDTH + plotWidth}
                y2={y}
                className="chart__grid"
              />
              <text
                x={AXIS_WIDTH - 8}
                y={y}
                textAnchor="end"
                dominantBaseline="central"
                className="chart__tick"
              >
                {compact(tick)}
              </text>
            </g>
          );
        })}

        {bars.map((bar, index) => {
          // Centre each column in its band, so the spacing stays even at any count.
          const x = AXIS_WIDTH + index * band + (band - column) / 2;
          const promptHeight = scale(bar.first);
          const completionHeight = Math.max(scale(bar.second) - SEGMENT_GAP, 0);
          const active = hovered === index;
          return (
            <g
              key={`${index}:${bar.label}`}
              className={`chart__bar${active ? " chart__bar--active" : ""}${onPick ? " chart__bar--pickable" : ""}`}
              onPointerEnter={() => onEnter(index)}
              onPointerMove={onMove}
              onPointerLeave={onLeave}
              onFocus={() => onEnter(index)}
              onBlur={onLeave}
              onClick={() => onPick?.(bar.label)}
              onKeyDown={(event) => activate(event, () => onPick?.(bar.label))}
              tabIndex={onPick ? 0 : -1}
              role={onPick ? "button" : undefined}
            >
              <rect
                x={AXIS_WIDTH + index * band}
                y={0}
                width={band}
                height={height}
                fill="transparent"
              />
              <rect
                x={x}
                y={baseline - promptHeight - completionHeight - SEGMENT_GAP}
                width={column}
                height={Math.max(completionHeight, 1)}
                rx={4}
                className="chart__fill chart__fill--completion"
              />
              <rect
                x={x}
                y={baseline - promptHeight}
                width={column}
                height={Math.max(promptHeight, 1)}
                className="chart__fill chart__fill--prompt"
              />
              <text
                x={x + column / 2}
                y={baseline + 16}
                textAnchor="middle"
                className="chart__category"
              >
                {/* MM-DD is enough on a dense axis; the full date is on hover and in the table. */}
                <title>{bar.label}</title>
                {fitText(shortDay(bar.label), band, LABEL_FONT)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/**
 * The breakdown, permanently on screen.
 *
 * It shows whichever bar is hovered or keyboard-focused, and the whole slice when neither. That
 * keeps the numbers readable without a pointer — which is what makes the tooltip an affordance
 * rather than the only way to see them — and stops the block appearing and disappearing, which
 * shifted everything below it each way.
 *
 * Value first, series name second: the reader already has the series and wants the number.
 */
function Readout({
  bar,
  bars,
  series,
}: {
  bar: UsageBar | null;
  bars: UsageBar[];
  series: SeriesLabels;
}): JSX.Element {
  const shown = bar ?? totalOf(bars);

  return (
    <dl className={`chart__readout${bar ? " chart__readout--focused" : ""}`} aria-live="polite">
      <div className="chart__readout-head">{shown.label}</div>
      {rows(shown, series)}
    </dl>
  );
}

/**
 * Make a focusable SVG group behave like a button.
 *
 * `tabIndex` makes a `<g>` focusable, but unlike a real button it fires no click on Enter or
 * Space — so without this the drill-down would be mouse-only while still advertising
 * `role="button"` to a screen reader.
 */
function activate(event: React.KeyboardEvent, run: () => void): void {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    run();
  }
}

/** "2026-08-21" reads as "08-21" on an axis; the year is the same for every column anyway. */
function shortDay(day: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day.slice(5) : day;
}

function compact(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}k`;
  }
  return String(value);
}

/** Round tick values, so the axis reads 0 / 5k / 10k rather than 0 / 4,913 / 9,826. */
function niceTicks(max: number): number[] {
  const magnitude = 10 ** Math.floor(Math.log10(max));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => max / s <= 4) ?? magnitude;
  const ticks: number[] = [];
  for (let value = 0; value <= max + step / 2; value += step) {
    ticks.push(Math.round(value));
  }
  return ticks;
}

/**
 * The rows a tooltip and the readout share.
 *
 * The split is only worth naming when there are two series; with one, the total *is* the series,
 * and repeating the same number under two headings pads the panel without telling the reader
 * anything.
 */
function rows(bar: UsageBar, series: SeriesLabels): JSX.Element[] {
  const shown: JSX.Element[] = [];
  if (series.second) {
    shown.push(
      <div key="first">
        <dt>
          <span className="chart__key chart__key--prompt" aria-hidden="true" />
          {series.first}
        </dt>
        <dd>{bar.first.toLocaleString()}</dd>
      </div>,
      <div key="second">
        <dt>
          <span className="chart__key chart__key--completion" aria-hidden="true" />
          {series.second}
        </dt>
        <dd>{bar.second.toLocaleString()}</dd>
      </div>,
    );
  }
  shown.push(
    <div key="total">
      <dt>{series.second ? "Total" : series.first}</dt>
      <dd>{bar.total.toLocaleString()}</dd>
    </div>,
  );
  for (const entry of bar.extra ?? []) {
    shown.push(
      <div key={entry.label}>
        <dt>{entry.label}</dt>
        <dd>{entry.value.toLocaleString()}</dd>
      </div>,
    );
  }
  return shown;
}

/** Every bar added up, for the readout when nothing is hovered or focused. */
function totalOf(bars: UsageBar[]): UsageBar {
  const extras = new Map<string, number>();
  let first = 0;
  let second = 0;
  let total = 0;
  for (const bar of bars) {
    first += bar.first;
    second += bar.second;
    total += bar.total;
    for (const entry of bar.extra ?? []) {
      extras.set(entry.label, (extras.get(entry.label) ?? 0) + entry.value);
    }
  }
  return {
    label: `all ${bars.length} ${bars.length === 1 ? "category" : "categories"}`,
    first,
    second,
    total,
    extra: [...extras].map(([label, value]) => ({ label, value })),
  };
}
